import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import TenantService from "../../services/tenantService";
import { tokens } from "../../theme";
import EditTenantInfo from "../../components/EditTenantInfo";
import BusinessIcon from "@mui/icons-material/Business";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import LockResetIcon from "@mui/icons-material/LockReset";
import PublicIcon from "@mui/icons-material/Public";
import TenantLayout from "../../layouts/TenantLayout";

export default function TenantProfile() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('tenantDashboard');

  const [tenantInfo, setTenantInfo] = useState({});
  const tenantService = useMemo(() => new TenantService(), []);
  const tenantId = typeof window !== "undefined" ? localStorage.getItem("tenantId") : null;
  const token = typeof window !== "undefined" ? localStorage.getItem("tokenTenant") : null;

  const [isEditing, setIsEditing] = useState(false);
  const [isChangePass, setIsChangePass] = useState(false);
  const [currentPassValue, setCurrentPassValue] = useState("");
  const [newPassValue, setNewPassValue] = useState("");
  const [confirmPassValue, setConfirmPassValue] = useState("");

  useEffect(() => {
    const fetchTenantInfo = async () => {
      try {
        const data = await tenantService.getTenantInfo(tenantId, token);
        setTenantInfo(data || {});
      } catch (error) {
        console.error("Tenant bilgileri çekilirken hata oluştu", error);
      }
    };
    if (tenantId && token) {
      fetchTenantInfo();
    }
  }, [tenantService, tenantId, token, isEditing, isChangePass]);

  const handleClosePasswordDialog = () => {
    setIsChangePass(false);
    setCurrentPassValue("");
    setNewPassValue("");
    setConfirmPassValue("");
  };

  const handleSavePassword = async () => {
    if (newPassValue !== confirmPassValue) {
      alert(t('passwordsDoNotMatch'));
      return;
    }

    const data = {
      oldPassword: currentPassValue,
      newPassword: newPassValue,
      confirmNewPassword: confirmPassValue,
    };

    try {
      await tenantService.updateTenant(data, tenantId, token);
      handleClosePasswordDialog();
    } catch (error) {
      console.error("Password update failed", error);
    }
  };

  const infoFields = useMemo(() => [
    {
      label: t('institutionName'),
      value: tenantInfo.name || "",
      icon: <BusinessIcon />,
    },
    {
      label: t('columnEmail'),
      value: tenantInfo.email || "",
      icon: <AlternateEmailIcon />,
    },
    {
      label: t('columnPhoneNumber'),
      value: tenantInfo.phone_number || "",
      icon: <PhoneIphoneIcon />,
    },
    {
      label: t('nationality') || "Nationality",
      value: tenantInfo.nationality || "",
      icon: <PublicIcon />,
    },
    {
      label: t('planType'),
      value: tenantInfo.plan_type || "",
      icon: <WorkspacePremiumIcon />,
    },
  ], [tenantInfo, t]);

  const profileCard = (
    <Box
      sx={{
        width: "100%",
        maxWidth: 960,
        borderRadius: 4,
        overflow: "hidden",
        backgroundColor: "#ffffff",
        boxShadow: `0 20px 60px rgba(15, 23, 42, 0.18)`,
        border: `1px solid ${colors.primary[300]}33`,
      }}
    >
      <Box
        sx={{
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          background: `linear-gradient(135deg, ${colors.primary[500]}dd 0%, ${colors.blueAccent[700]}cc 100%)`,
          color: colors.grey[100],
          position: "relative",
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems="center">
          <Avatar
            sx={{
              width: 82,
              height: 82,
              background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[500]} 100%)`,
              boxShadow: `0 18px 40px rgba(37, 99, 235, 0.4)`,
              border: `3px solid rgba(255,255,255,0.4)`,
              fontSize: "1.8rem",
              fontWeight: 700,
            }}
          >
            {tenantInfo?.name?.slice(0, 2)?.toUpperCase() || "TN"}
          </Avatar>

          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.4,
              }}
            >
              {t('institutionAccount')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                opacity: 0.85,
                mt: 1,
                maxWidth: 520,
              }}
            >
              {t('manageOrganizationDetails')}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 3, md: 5 }, py: { xs: 4, md: 5 } }}>
        <Grid container spacing={3.5}>
          {infoFields.map((field) => (
            <Grid item xs={12} sm={6} key={field.label}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.2,
                  p: 2.5,
                  borderRadius: 3,
                  background: "#f8fafc",
                  border: `1px solid ${colors.primary[300]}33`,
                  boxShadow: `0 8px 24px rgba(15, 23, 42, 0.06)`,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: `${colors.blueAccent[700]}22`,
                      color: colors.blueAccent[300],
                    }}
                    variant="rounded"
                  >
                    {field.icon}
                  </Avatar>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                      color: colors.grey[600],
                      fontWeight: 600,
                    }}
                  >
                    {field.label}
                  </Typography>
                </Stack>

                <TextField
                  value={field.value}
                  disabled
                  fullWidth
                  size="small"
                  InputProps={{
                    sx: {
                      borderRadius: 2,
                      backgroundColor: "#ffffff",
                      color: colors.grey[900],
                      "& fieldset": {
                        borderColor: `${colors.primary[200]}40`,
                      },
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: `${colors.grey[800]} !important`,
                        opacity: 1,
                      },
                    },
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2.5,
            mt: 4,
            borderTop: `1px dashed ${colors.primary[300]}66`,
            pt: 3.5,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: colors.primary[800],
              }}
            >
              {t('manageAccountSettings')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.grey[600], maxWidth: 420, mt: 0.5 }}
            >
              {t('updateOrganizationInfo')}
            </Typography>
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            justifyContent="flex-end"
          >
            <Button
              onClick={() => setIsEditing(true)}
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                letterSpacing: 0.4,
                borderRadius: 3,
                px: 3,
                py: 1.3,
                background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[500]} 100%)`,
                boxShadow: `0 12px 28px rgba(37, 99, 235, 0.25)`,
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: `0 18px 34px rgba(37, 99, 235, 0.32)`,
                },
              }}
            >
              {t('editInformation')}
            </Button>
            <Button
              onClick={() => setIsChangePass(true)}
              variant="outlined"
              startIcon={<LockResetIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                letterSpacing: 0.4,
                borderRadius: 3,
                px: 3,
                py: 1.3,
                borderColor: `${colors.blueAccent[600]}55`,
                color: colors.blueAccent[500],
                "&:hover": {
                  borderColor: colors.blueAccent[500],
                  backgroundColor: `${colors.blueAccent[600]}15`,
                },
              }}
            >
              {t('changePassword')}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <TenantLayout
        contentSx={{
          backgroundColor: "#f5f7fb",
          display: "flex",
          justifyContent: "center",
          px: { xs: 2, md: 4, lg: 6 },
          py: { xs: 3, md: 5 },
        }}
      >
        {profileCard}
      </TenantLayout>

    <Dialog
        open={isEditing}
        onClose={() => setIsEditing(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 1,
            background: `linear-gradient(135deg, ${colors.primary[500]}ee 0%, ${colors.primary[600]}f5 60%, #ffffff 100%)`,
            boxShadow: `0 28px 60px rgba(15, 23, 42, 0.25)`,
            border: `1px solid ${colors.primary[300]}66`,
          },
        }}
      >
        <DialogContent sx={{ py: 3, px: { xs: 2, md: 4 } }}>
          <EditTenantInfo changeEditState={(state) => setIsEditing(state)} />
        </DialogContent>
      </Dialog>

      <Dialog
        open={isChangePass}
        onClose={handleClosePasswordDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: "#f9fafc",
            boxShadow: `0 20px 50px rgba(15, 23, 42, 0.25)`,
            border: `1px solid ${colors.primary[200]}55`,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            color: colors.primary[800],
            pb: 1,
          }}
        >
          {t('updatePassword')}
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 3 }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.7, color: colors.grey[700] }}>
                {t('currentPassword')}
              </Typography>
              <TextField
                type="password"
                fullWidth
                size="small"
                value={currentPassValue}
                onChange={(e) => setCurrentPassValue(e.target.value)}
                InputProps={{
                  sx: {
                    borderRadius: 2,
                    backgroundColor: "#ffffff",
                    "& .MuiInputBase-input": {
                      color: colors.grey[900],
                    },
                    "& fieldset": {
                      borderColor: `${colors.primary[200]}55`,
                    },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.7, color: colors.grey[700] }}>
                {t('newPassword')}
              </Typography>
              <TextField
                type="password"
                fullWidth
                size="small"
                value={newPassValue}
                onChange={(e) => setNewPassValue(e.target.value)}
                InputProps={{
                  sx: {
                    borderRadius: 2,
                    backgroundColor: "#ffffff",
                    "& .MuiInputBase-input": {
                      color: colors.grey[900],
                    },
                    "& fieldset": {
                      borderColor: `${colors.primary[200]}55`,
                    },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.7, color: colors.grey[700] }}>
                {t('confirmPassword')}
              </Typography>
              <TextField
                type="password"
                fullWidth
                size="small"
                value={confirmPassValue}
                onChange={(e) => setConfirmPassValue(e.target.value)}
                InputProps={{
                  sx: {
                    borderRadius: 2,
                    backgroundColor: "#ffffff",
                    "& .MuiInputBase-input": {
                      color: colors.grey[900],
                    },
                    "& fieldset": {
                      borderColor: `${colors.primary[200]}55`,
                    },
                  },
                }}
              />
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="flex-end">
              <Button
                onClick={handleClosePasswordDialog}
                variant="outlined"
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 3,
                  borderColor: `${colors.primary[300]}66`,
                  color: colors.grey[700],
                  "&:hover": {
                    borderColor: colors.primary[400],
                    backgroundColor: `${colors.primary[200]}33`,
                  },
                }}
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={handleSavePassword}
                variant="contained"
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 3,
                  background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[500]} 100%)`,
                  boxShadow: `0 12px 28px rgba(37, 99, 235, 0.25)`,
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 18px 34px rgba(37, 99, 235, 0.32)`,
                  },
                }}
              >
                {t('saveChanges')}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
